import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { API } from '../../../../../api';
import { isVersioningOffError } from '../../../../../utils/courseVersioningSession';
import {
  toVersionEntry,
  VersionEntry,
  VersionPreviewEntry
} from '../../Program/components/program tabs/ProgramSettings/versioning/CourseVersioningContext';
import type { VersionHistorySource } from '../../Program/components/program tabs/ProgramSettings/versioning/VersionHistoryDrawer';
import type { VersionPreviewSource } from '../../Program/components/program tabs/ProgramSettings/versioning/VersionPreview';
import { generationModulesToDrafts } from './generationDrafts';

const VERSIONS_PAGE = 50;

export const WIZARD_VERSION_HINTS = [
  'A version is kept when you save, before a rewrite by Elliott, and when you leave the tab.',
  'Click a version to preview it. Nothing changes until you restore.',
  'Restoring updates this program build only. Nothing reaches learners until you publish.'
];

export interface WizardVersionHistory {
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  toggle: () => void;
  /** Close the panel and drop any preview (leaving the Structure / Content steps). */
  close: () => void;
  previewEntry: VersionPreviewEntry | null;
  restoring: boolean;
  refresh: () => Promise<void>;
  /** Data source for VersionHistoryDrawer. */
  drawer: VersionHistorySource;
  /** Data source for VersionPreview. */
  preview: VersionPreviewSource;
}

interface Options {
  generationId: string | null;
  /** Org flag on, org not frozen, generation exists. */
  enabled: boolean;
  /**
   * Flush unsaved wizard edits so the BE's `before_restore` version carries
   * them. Resolve false to abort the restore (nothing is restored then).
   */
  beforeRestore: () => Promise<boolean>;
  /** Reload the wizard from GET program-generation/{id}/status after a restore. */
  onRestored: () => Promise<void>;
}

/**
 * Version History for the AIMD wizard (docs/API-course-versioning.md §2b):
 * lists the generation's versions, serves the read-only preview of one, and
 * restores it. Everything here is read-only towards the wizard: the preview
 * is a mapped copy of `snapshot.modules`, and a restore goes through the API
 * followed by the caller's reload, so no updateContent / updateStructure call
 * can trigger the wizard's automatic generation.
 */
export function useWizardVersionHistory({
  generationId,
  enabled,
  beforeRestore,
  onRestored
}: Options): WizardVersionHistory {
  const [entries, setEntries] = useState<VersionEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [previewEntry, setPreviewEntry] = useState<VersionPreviewEntry | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  const unmountedRef = useRef(false);
  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    []
  );
  const beforeRestoreRef = useRef(beforeRestore);
  beforeRestoreRef.current = beforeRestore;
  const onRestoredRef = useRef(onRestored);
  onRestoredRef.current = onRestored;

  const refresh = useCallback(async () => {
    if (!enabled || !generationId) return;
    setEntriesLoading(true);
    try {
      const res = await API.listProgramGenerationVersions(generationId, { limit: VERSIONS_PAGE });
      const data = res?.data?.data;
      if (unmountedRef.current || !data) return;
      setEntries((data.versions || []).map(toVersionEntry));
    } catch {
      // VERSIONING_OFF already switched the feature off for the session
      // (the button and the drawer go away); anything else keeps the list.
    } finally {
      if (!unmountedRef.current) setEntriesLoading(false);
    }
  }, [enabled, generationId]);

  // Fresh list whenever the panel opens; a new generation starts empty.
  useEffect(() => {
    if (panelOpen) refresh();
  }, [panelOpen, refresh]);
  useEffect(() => {
    setEntries([]);
    setPreviewEntry(null);
  }, [generationId]);
  useEffect(() => {
    if (!enabled) {
      setPanelOpen(false);
      setPreviewEntry(null);
    }
  }, [enabled]);

  const openPreview = useCallback(
    async (id: string) => {
      if (!generationId) return;
      const entry = entries.find((e) => e.id === id);
      if (!entry || previewEntry?.id === id) return;
      setPreviewLoadingId(id);
      try {
        const res = await API.getProgramGenerationVersion(generationId, id);
        const version = res?.data?.data;
        if (unmountedRef.current || !version) return;
        setPreviewEntry({ ...entry, snapshot: generationModulesToDrafts(version.snapshot?.modules) });
      } catch (err) {
        if (isVersioningOffError(err)) return;
        toast.error('Could not load that version.');
      } finally {
        if (!unmountedRef.current) setPreviewLoadingId(null);
      }
    },
    [entries, generationId, previewEntry?.id]
  );

  const resume = useCallback(() => {
    if (!previewEntry) return;
    setPreviewEntry(null);
    toast.info('Back to your current program. Nothing was reloaded or rebuilt.');
  }, [previewEntry]);

  const restore = useCallback(async () => {
    if (!previewEntry || restoring || !generationId) return;
    const entry = previewEntry;
    setRestoring(true);
    try {
      const flushed = await beforeRestoreRef.current();
      if (!flushed) {
        toast.error("Your latest edits couldn't be saved, so nothing was restored. Please try again.");
        return;
      }
      await API.restoreProgramGenerationVersion(generationId, entry.id);
      setPreviewEntry(null);
      await onRestoredRef.current();
      refresh();
      toast.success(
        `Restored the ${entry.at.toLocaleString('en-US', {
          weekday: 'short',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })} version. Nothing reaches learners until you publish.`
      );
    } catch (err: any) {
      if (isVersioningOffError(err)) return;
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.code === 'EDIT_LOCK') {
        toast.warn(
          `${
            data?.lock?.name || 'Another admin'
          } is editing this program in the Designer, so restore is unavailable right now.`
        );
        return;
      }
      // 423 (editing frozen) is toasted by the axios interceptor.
      if (status !== 423) toast.error(data?.message || 'Could not restore that version.');
    } finally {
      if (!unmountedRef.current) setRestoring(false);
    }
  }, [previewEntry, restoring, generationId, refresh]);

  const toggle = useCallback(() => setPanelOpen((open) => !open), []);
  const close = useCallback(() => {
    setPanelOpen(false);
    setPreviewEntry(null);
  }, []);

  const drawer = useMemo<VersionHistorySource>(
    () => ({ entries, entriesLoading, panelOpen, setPanelOpen, previewEntry, previewLoadingId, openPreview, resume }),
    [entries, entriesLoading, panelOpen, previewEntry, previewLoadingId, openPreview, resume]
  );
  const preview = useMemo<VersionPreviewSource>(
    () => ({ previewEntry, resume, restore, readOnly: restoring, lockHolder: null }),
    [previewEntry, resume, restore, restoring]
  );

  return { panelOpen, setPanelOpen, toggle, close, previewEntry, restoring, refresh, drawer, preview };
}
